import React, { Suspense } from 'react';
import {
    ModalCloseButton,
    HStack,
    ModalBody,
    ModalContent,
    Modal,
    useDisclosure,
    IconButton,
    Icon,
    Center,
    Drawer,
    DrawerContent,
    DrawerBody,
    DrawerCloseButton,
} from '@chakra-ui/react';
import { CardSpinner } from '../../../../../../../domain';
import {
    RecordOverlayContainerProps,
    RecordOverlayViewProps,
} from './recordOverlayProps';
import { ReportingContainerBaseConfig } from '../../../base';
import { RecordOverlayController } from './recordOverlayInterface';
import { RecordOverlayLoader } from './recordOverlayInterface';
import { useSearchParams } from 'react-router-dom';
import { RecordOverlayParameterSchema } from './recordOverlaySchema';
import { AiOutlineClose } from 'react-icons/ai';
import { z } from 'zod';

export function createRecordOverlayContainer(
    config: ReportingContainerBaseConfig & {
        keys: {
            recordUrlParam: string;
        };
    },
    loader: RecordOverlayLoader,
    controller: RecordOverlayController,
    View: React.FC<RecordOverlayViewProps>
): React.FC<RecordOverlayContainerProps> {
    const {
        kernel: {
            infra: {
                optionParser: { useOptions },
            },
        },
        context: { useContext },
    } = config;

    const RecordOverlayParameterSchema = z.object({
        [config.keys.recordUrlParam]: z.string().optional(),
    });

    const Main: React.FC<RecordOverlayContainerProps & { content: string }> = ({
        ...containerProps
    }) => {
        const context = useContext();
        const [searchParams, setSearchParams] = useSearchParams();
        const data = loader.useLoad(context, {
            record: {
                id: containerProps.content,
            },
        });
        const props = controller.useProps(context, data, {
            fullscreen: {
                link: {
                    to: `/reporting/reports/${containerProps.content}`,
                },
            },
        });
        return <View {...props} />;
    };

    return (props) => {
        const [searchParams, setSearchParams] = useSearchParams();
        const options = useOptions(RecordOverlayParameterSchema);
        const recordId = options[config.keys.recordUrlParam];
        return (
            <Drawer
                placement="right"
                isOpen={!!recordId}
                onClose={() => {
                    searchParams.delete(config.keys.recordUrlParam);
                    return setSearchParams(searchParams, { replace: true });
                }}
                size={{ base: 'md', xl: 'lg', '2xl': 'xl' }}
                returnFocusOnClose={false}
            >
                <DrawerContent
                    bg="black"
                    _focus={{ outline: 'none' }}
                    position="relative"
                    p={0}
                >
                    <DrawerBody
                        bg="whiteAlpha.100"
                        p={{ base: 4, md: 8, xl: 12, '2xl': 16 }}
                    >
                        <DrawerCloseButton
                            as={IconButton}
                            icon={<Icon as={AiOutlineClose} />}
                            variant="ghost"
                            color="whiteAlpha.700"
                            borderWidth={1}
                            position="fixed"
                            top={4}
                            right={4}
                            borderColor="whiteAlpha.200"
                            borderRadius="full"
                            size="lg"
                            bg="none"
                            _hover={{ bg: 'whiteAlpha.50' }}
                            _focus={{ outline: 'none', shadow: 'none' }}
                        />
                        <Suspense
                            fallback={
                                <Center w="100vw" h="100vh">
                                    <HStack w="full" align="center" justify="center">
                                        <CardSpinner />
                                    </HStack>
                                </Center>
                            }
                        >
                            {recordId && <Main content={recordId} />}
                        </Suspense>
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        );
    };
}
